import clsx from 'clsx';
import { Dispatch, ReactNode, SetStateAction, useRef, useState } from 'react';
import { PicturePicker } from '../../sop-workflow/escalate/PicturePicker';
import { badge } from '../../sop-workflow/escalate/util';
import { usePasteImages, validateImageFile } from '../../sop-workflow/util';
import { ACCEPTED_TYPES, MAX_TOTAL_BYTES } from './constants';
import { attachmentState } from './sms-util';

interface Props {
  children: ReactNode;
  fileList: File[];
  previewList: string[];
  onFilesSelect: (files: File[]) => void;
  onFileRemove: (idx: number) => void;
  setFiles: Dispatch<SetStateAction<File[]>>;
  onFileError: (error: string | null) => void;
}

const toMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1);

export function SMSAttachments(props: Props) {
  const {
    children,
    fileList,
    previewList,
    onFilesSelect,
    onFileRemove,
    setFiles,
    onFileError,
  } = props;
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  usePasteImages(setFiles);

  const { totalBytes, overBytes } = attachmentState(fileList);

  const onValidatedFilesSelect = async (newFiles: File[]) => {
    const accepted: File[] = [];
    const errors: string[] = [];
    for (const file of newFiles) {
      const error = await validateImageFile(file, [...fileList, ...accepted], {
        acceptedTypes: ACCEPTED_TYPES,
        maxBytes: MAX_TOTAL_BYTES,
      });
      if (error) errors.push(error);
      else accepted.push(file);
    }
    onFileError(errors.length ? errors[errors.length - 1] : null);
    if (accepted.length) onFilesSelect(accepted);
  };

  const onRemoveFile = (idx: number) => {
    onFileRemove(idx);
    onFileError(null);
  };

  const hasImages = fileList.length > 0;

  return (
    <div>
      {children}
      <div className="mb-1.5 mt-3 flex items-center gap-2">
        <span>Photos</span>
        {hasImages && (
          <span className="bg-onlight-bg-3 dark:bg-ondark-bg-3 text-onlight-text-2 dark:text-ondark-text-2 inline-flex items-center rounded-3xl px-2.5 py-0.5 text-xs">
            {fileList.length} attached
          </span>
        )}
      </div>
      <PicturePicker
        badge={badge(1, hasImages)}
        showHeader={false}
        noIndent
        dashedEmptyTile
        previewList={previewList}
        fileInputRef={fileInputRef}
        previewIdx={previewIdx}
        setPreviewIdx={setPreviewIdx}
        onFilesSelect={onValidatedFilesSelect}
        onFileRemove={onRemoveFile}
      />
      <div
        className={clsx(
          'pt-1 text-right text-xs',
          overBytes
            ? 'text-alert-600 dark:text-alert-400'
            : 'text-onlight-text-3 dark:text-ondark-text-3'
        )}
      >
        {toMB(totalBytes)}/5 MB
      </div>
    </div>
  );
}
